/* eslint-disable max-lines */
import React, {
 useState
} from 'react';
import {
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from 'shared/components/ui/table';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Trash2, Check, KeyRound
} from 'lucide-react';
import {
 SchemaField
} from '../../types/schema.types';
import {
    dataTypes,
    DATA_TYPE_CONFIGS,
    DATA_TYPE_DEFAULTS,
} from 'builders/builderApp/DataBuilder/Tables/components/table-fields/constants';

interface SchemaFieldsTabProps {
    fields: SchemaField[];
    setFields: React.Dispatch<React.SetStateAction<SchemaField[]>>;
    isNewSchema: boolean;
}

let fieldIdCounter = 0;
function generateFieldId(): string {
    fieldIdCounter += 1;
    return `field_${Date.now()}_${fieldIdCounter}`;
}

const SchemaFieldsTab: React.FC<SchemaFieldsTabProps> = ({
    fields, setFields,
}) => {
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    const selectedField = fields.find((f) => f.id === selectedFieldId) || null;

    const handleAddField = () => {
        const newField: SchemaField = {
            id: generateFieldId(),
            columnName: 'new_field',
            dataType: 'string',
            length: DATA_TYPE_CONFIGS.string.defaultLength,
            fieldsAccessBy: [],
            description: '',
            readOnly: false,
            visibleStatus: true,
            format: '',
            default: DATA_TYPE_DEFAULTS.string,
            constraints: [],
            references: {
 type: '',
table: '',
column: '',
values: []
},
            validation: [],
            isNew: true,
            isModified: false,
        };
        setFields((prev) => [...prev, newField]);
        setSelectedFieldId(newField.id);
    };

    const handleDeleteField = (fieldId: string) => {
        setFields((prev) => prev.filter((f) => f.id !== fieldId));
        if (selectedFieldId === fieldId) {
            setSelectedFieldId(null);
        }
    };

    const updateField = (fieldId: string, updates: Partial<SchemaField>) => {
        setFields((prev) =>
            prev.map((f) =>
                f.id === fieldId
                    ? {
 ...f,
...updates,
isModified: !f.isNew ? true : f.isModified
}
                    : f
            )
        );
    };

    const isRequired = (field: SchemaField) => field.constraints?.includes('NOT NULL');
    const isPrimaryKey = (field: SchemaField) =>
        field.constraints?.includes('UNIQUE') && field.constraints?.includes('NOT NULL');

    const toggleConstraint = (field: SchemaField, constraint: string) => {
        const has = field.constraints?.includes(constraint);
        const newConstraints = has
            ? field.constraints.filter((c) => c !== constraint)
            : [...(field.constraints || []), constraint];
        updateField(field.id, {
 constraints: newConstraints
});
    };

    const getDataTypeColor = (type: string): string => {
        const dt = dataTypes.find((d) => d.value === type);
        return dt?.color || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="flex gap-4 min-h-[500px]">
            {/* Left Panel - Fields Table */}
            <div className="flex-1 border rounded-md flex flex-col">
                <div className="flex items-center justify-between p-3 border-b">
                    <h3 className="text-sm font-semibold">Fields</h3>
                    <Button size="sm" variant="outline" onClick={handleAddField}>
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add Field
                    </Button>
                </div>

                {fields.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                        <p className="text-sm text-muted-foreground">No fields defined yet.</p>
                        <Button size="sm" onClick={handleAddField}>
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Add Your First Field
                        </Button>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Field Name</TableHead>
                                <TableHead>Data Type</TableHead>
                                <TableHead className="w-[60px] text-center">Req</TableHead>
                                <TableHead className="w-[60px] text-center">PK</TableHead>
                                <TableHead className="w-[40px]" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {fields.map((field) => (
                                <TableRow
                                    key={field.id}
                                    className={`cursor-pointer ${selectedFieldId === field.id ? 'bg-muted/50' : ''}`}
                                    onClick={() => setSelectedFieldId(field.id)}
                                >
                                    <TableCell className="font-medium">
                                        {field.columnName}
                                        {field.isNew && (
                                            <Badge variant="outline" className="ml-2 text-xs bg-green-50 text-green-700">
                                                new
                                            </Badge>
                                        )}
                                        {field.isModified && !field.isNew && (
                                            <Badge variant="outline" className="ml-2 text-xs bg-yellow-50 text-yellow-700">
                                                modified
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className={`${getDataTypeColor(field.dataType)} font-medium`}>
                                            {field.dataType}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {isRequired(field) && <Check className="h-4 w-4 mx-auto text-green-600" />}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {isPrimaryKey(field) && <KeyRound className="h-4 w-4 mx-auto text-amber-600" />}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-red-500 hover:text-red-700"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteField(field.id); }}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Right Panel - Field Detail */}
            <div className="w-[400px] shrink-0 border rounded-md flex flex-col">
                {selectedField ? (
                    <div className="p-4 space-y-5 overflow-y-auto">
                        <h3 className="text-sm font-semibold border-b pb-2">Field Configuration</h3>

                        {/* Basic Section */}
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Field Name</Label>
                                <Input
                                    value={selectedField.columnName}
                                    onChange={(e) => updateField(selectedField.id, {
 columnName: e.target.value
})}
                                    placeholder="field_name"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-xs">Data Type</Label>
                                <Select
                                    value={selectedField.dataType}
                                    onValueChange={(value) => {
                                        const config = DATA_TYPE_CONFIGS[value];
                                        updateField(selectedField.id, {
                                            dataType: value,
                                            length: config?.defaultLength || 0,
                                            default: DATA_TYPE_DEFAULTS[value] ?? '',
                                        });
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {dataTypes.map((dt) => (
                                            <SelectItem key={dt.value} value={dt.value}>
                                                {dt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {DATA_TYPE_CONFIGS[selectedField.dataType]?.hasLength && (
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Length</Label>
                                    <Input
                                        type="number"
                                        value={selectedField.length}
                                        onChange={(e) => updateField(selectedField.id, {
 length: Number(e.target.value)
})}
                                    />
                                </div>
                            )}

                            <div className="space-y-1.5">
                                <Label className="text-xs">Description</Label>
                                <Textarea
                                    value={selectedField.description}
                                    onChange={(e) => updateField(selectedField.id, {
 description: e.target.value
})}
                                    placeholder="Describe this field..."
                                    rows={2}
                                    className="resize-none"
                                />
                            </div>
                        </div>

                        {/* Constraints Section */}
                        <div className="space-y-3">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                Constraints
                            </h4>
                            <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Required (NOT NULL)</Label>
                                    <Switch
                                        checked={isRequired(selectedField)}
                                        onCheckedChange={() => toggleConstraint(selectedField, 'NOT NULL')}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Unique</Label>
                                    <Switch
                                        checked={selectedField.constraints?.includes('UNIQUE')}
                                        onCheckedChange={() => toggleConstraint(selectedField, 'UNIQUE')}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Index</Label>
                                    <Switch
                                        checked={selectedField.constraints?.includes('INDEX')}
                                        onCheckedChange={() => toggleConstraint(selectedField, 'INDEX')}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Case Sensitive</Label>
                                    <Switch
                                        checked={selectedField.constraints?.includes('CASESENSITIVE')}
                                        onCheckedChange={() => toggleConstraint(selectedField, 'CASESENSITIVE')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Default & Validation Section */}
                        <div className="space-y-3">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                Default & Validation
                            </h4>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Default Value</Label>
                                {selectedField.dataType === 'boolean' ? (
                                    <Select
                                        value={String(selectedField.default)}
                                        onValueChange={(v) => updateField(selectedField.id, {
 default: v
})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">True</SelectItem>
                                            <SelectItem value="false">False</SelectItem>
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        value={String(selectedField.default || '')}
                                        onChange={(e) => updateField(selectedField.id, {
 default: e.target.value
})}
                                        placeholder="Default value"
                                    />
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Validation (Regex)</Label>
                                <Input
                                    value={Array.isArray(selectedField.validation) ? selectedField.validation.flat().join('') : ''}
                                    onChange={(e) => updateField(selectedField.id, {
 validation: e.target.value ? [e.target.value] : []
})}
                                    placeholder="e.g., ^[a-zA-Z0-9]+$"
                                    className="font-mono text-xs"
                                />
                            </div>
                        </div>

                        {/* Format Section */}
                        {['date', 'time', 'datetime'].includes(selectedField.dataType) && (
                            <div className="space-y-3">
                                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Format
                                </h4>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Display Format</Label>
                                    <Input
                                        value={selectedField.format}
                                        onChange={(e) => updateField(selectedField.id, {
 format: e.target.value
})}
                                        placeholder="e.g., YYYY-MM-DD"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center p-4">
                        <p className="text-sm text-muted-foreground">
                            Select a field to view and edit its configuration
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchemaFieldsTab;
